<script setup lang="ts">
import sprite from '@/assets/icons/sprite.svg';
import { IconName, IconSize } from '@/models/icon-types';

function dataURItoBlobUrl(dataURI: string): string {
  const svg = decodeURI(dataURI).split(',')[1];
  if (!svg) {
    return dataURI;
  }
  const blob = new Blob([svg], { type: 'image/svg+xml' });

  return URL.createObjectURL(blob);
}

const props = withDefaults(
  defineProps<{
    name: IconName;
    size?: IconSize;
    className?: string;
    clickable?: boolean;
    onClick?: () => void;
  }>(),
  {
    size: IconSize.BodyM
  }
);
const spriteUrl = dataURItoBlobUrl(`${sprite}`);

const handleClick = () => {
  if (props.onClick) {
    props.onClick();
  }
};
</script>

<template>
  <div
    :class="{
      'app-icon': true,
      'clickable-icon': props.clickable,
      [`app-icon--${size}`]: size,
      [className ?? '']: className
    }"
    @click="handleClick"
  >
    <svg
      xmlns="http://www.w3.org/2000/svg"
      :class="{ 'app-icon': true, [`app-icon--${size}`]: size }"
      role="img"
      :aria-label="name"
    >
      <use :href="`${spriteUrl}#${name}`"></use>
    </svg>
  </div>
</template>

<style scoped lang="scss">
.app-icon {
  width: $body-m-font-size;
  height: $body-m-font-size;

  &--headline-xl {
    width: $headline-xl-font-size;
    height: $headline-xl-font-size;
  }

  &--headline-l {
    width: $headline-l-font-size;
    height: $headline-l-font-size;
  }

  &--headline-m {
    width: $headline-m-font-size;
    height: $headline-m-font-size;
  }

  &--headline-s {
    width: $headline-s-font-size;
    height: $headline-s-font-size;
  }

  &--headline-xs {
    width: $headline-xs-font-size;
    height: $headline-xs-font-size;
  }

  &--body-xl {
    width: $body-xl-font-size;
    height: $body-xl-font-size;
  }

  &--body-l {
    width: $body-l-font-size;
    height: $body-l-font-size;
  }

  &--body-m {
    width: $body-m-font-size;
    height: $body-m-font-size;
  }

  &--body-s {
    width: $body-s-font-size;
    height: $body-s-font-size;
  }

  &--body-xs {
    width: $body-xs-font-size;
    height: $body-xs-font-size;
  }

  &--link-l {
    width: $link-l-font-size;
    height: $link-l-font-size;
  }

  &--link-m {
    width: $link-m-font-size;
    height: $link-m-font-size;
  }

  &--link-s {
    width: $link-s-font-size;
    height: $link-s-font-size;
  }

  &--link-xs {
    width: $link-xs-font-size;
    height: $link-xs-font-size;
  }
}

.clickable-icon {
  &:hover {
    color: $color-icon-dark;
    cursor: pointer;
    transform: scale(1.1);
    transition: transform 0.1s ease-in;
  }
}
</style>
